import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faPlus,
  faPen,
  faTrash,
  faCheckCircle,
  faCircleExclamation,
  faSearch,
  faTv,
  faFilter,
  faRotate,
  faVideo,
  faLocationDot,
  faClock,
  faStore,
  faSortNumericDown,
  faSortNumericUp,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

import useFetch from "../../hooks/useFetch";
import { FILTV_CHANNELS_URL, FILTV_ADS_URL } from "./filtvConstants";
import FILTVAdManagerForm from "./FILTVAdManagerForm";

/* -------------------------------------------------------------------------- */
/* Empty Advertisement                                                        */
/* -------------------------------------------------------------------------- */

const emptyForm = {
  advertisementCode: "",
  cartProductCode: "",
  lcn: "",
  merchant: "",
  area: "",
  city: "",
  state: "",
  pincode: "",
  videoType: "youtube",
  videoLink: "",
  offer: "",
  offerEndsAt: "",
  phone: "",
  whatsapp: "",
  isActive: true,
};

/* -------------------------------------------------------------------------- */
/* Component                                                                   */
/* -------------------------------------------------------------------------- */

const FILTVAdManager = () => {
  const authState = useSelector((state) => state.authReducer);
  // console.log("authState →", authState);
// console.log("token →", authState?.token);
  const [fetchData] = useFetch();

  /* ---------------------------------------------------------------------- */
  /* Data                                                                    */
  /* ---------------------------------------------------------------------- */

  const [channels, setChannels] = useState([]);
  const [advertisements, setAdvertisements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  /* ---------------------------------------------------------------------- */
  /* UI                                                                      */
  /* ---------------------------------------------------------------------- */

  const [message, setMessage] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  /* ---------------------------------------------------------------------- */
  /* Filters                                                                 */
  /* ---------------------------------------------------------------------- */

  const [search, setSearch] = useState("");
  const [channelFilter, setChannelFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sortBy, setSortBy] = useState("LCN_ASC");

  /* ---------------------------------------------------------------------- */
  /* Form                                                                    */
  /* ---------------------------------------------------------------------- */

  const [form, setForm] = useState(emptyForm);

  /* ---------------------------------------------------------------------- */
  /* Load Everything                                                         */
  /* ---------------------------------------------------------------------- */

  const load = async () => {
    setLoading(true);
    setMessage(null);

    try {
      const [channelRes, adRes] = await Promise.all([
        fetchData({
          url: FILTV_CHANNELS_URL,
          method: "get",
          token: authState?.token,
        }),
        fetchData({
          url: FILTV_ADS_URL,
          method: "get",
          token: authState?.token,
        }),
      ]);

      setChannels(channelRes || []);
      setAdvertisements(adRes || []);
    } catch (err) {
      console.error(err);
      setMessage({
        type: "error",
        text: "Unable to load advertisements.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  /* ---------------------------------------------------------------------- */
  /* Helpers                                                                 */
  /* ---------------------------------------------------------------------- */

  const getChannel = (lcn) => {
    return channels.find((c) => c.lcn === lcn);
  };

  const resetForm = () => {
    setEditingCode(null);
    setForm(emptyForm);
    setShowForm(false);
  };

  const openAddForm = () => {
    setEditingCode(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setSaving(true);
  setMessage(null);

  try {
    if (
      !form.advertisementCode.trim() ||
      !form.cartProductCode.trim() ||
      !form.merchant.trim() ||
      !form.lcn ||
      !form.videoLink.trim()
    ) {
      setMessage({
        type: "error",
        text: "Advertisement Code, Product Code, Merchant, Channel and Video Link are mandatory.",
      });
      setSaving(false);
      return;
    }

    const payload = {
      advertisementCode: form.advertisementCode,
      cartProductCode: form.cartProductCode,
      lcn: Number(form.lcn),
      merchant: form.merchant,
      area: form.area,
      city: form.city,
      state: form.state,
      pincode: form.pincode,
      videoType: form.videoType,
      videoLink: form.videoLink,
      offer: form.offer,
      offerEndsAt: form.offerEndsAt || null,
      phone: form.phone,
      whatsapp: form.whatsapp,
      isActive: form.isActive,
    };

    let response;

    if (editingCode) {
      response = await fetchData({
        url: `${FILTV_ADS_URL}/${editingCode}`,
        method: "patch",
        body: payload,
        token: authState?.token,
      });
    } else {
      response = await fetchData({
        url: FILTV_ADS_URL,
        method: "post",
        body: payload,
        token: authState?.token,
      });
    }

    if (response) {
      setMessage({
        type: "success",
        text: editingCode
          ? "Advertisement Updated Successfully."
          : "Advertisement Created Successfully.",
      });
      await load();
      resetForm();
    } else {
      setMessage({
        type: "error",
        text: "Unable to save advertisement.",
      });
    }
  } catch (err) {
    console.error(err);
    setMessage({
      type: "error",
      text: "Server error while saving advertisement.",
    });
  } finally {
    setSaving(false);
  }
};

  const openEditForm = (ad) => {
    setEditingCode(ad.advertisementCode);
    setForm({
      advertisementCode: ad.advertisementCode,
      cartProductCode: ad.cartProductCode,
      lcn: ad.lcn,
      merchant: ad.merchant,
      area: ad.area || "",
      city: ad.city || "",
      state: ad.state || "",
      pincode: ad.pincode || "",
      videoType: ad.videoType,
      videoLink: ad.videoLink,
      offer: ad.offer || "",
      offerEndsAt: ad.offerEndsAt ? ad.offerEndsAt.substring(0, 16) : "",
      phone: ad.phone || "",
      whatsapp: ad.whatsapp || "",
      isActive: ad.isActive,
    });
    setShowForm(true);
  };

  /* ---------------------------------------------------------------------- */
  /* Statistics                                                              */
  /* ---------------------------------------------------------------------- */

  const totalAds = advertisements.length;
  const activeAds = advertisements.filter((a) => a.isActive).length;
  const inactiveAds = advertisements.filter((a) => !a.isActive).length;
  const uniqueChannels = [...new Set(advertisements.map((a) => a.lcn))].length;

  /* ---------------------------------------------------------------------- */
  /* Filtering                                                               */
  /* ---------------------------------------------------------------------- */

  const filteredAds = useMemo(() => {
    let list = [...advertisements];

    if (statusFilter !== "All") {
      list = list.filter((ad) =>
        statusFilter === "Active" ? ad.isActive : !ad.isActive
      );
    }

    if (channelFilter !== "All") {
      list = list.filter((ad) => Number(channelFilter) === ad.lcn);
    }

    if (search.trim()) {
      const keyword = search.toLowerCase();
      list = list.filter((ad) =>
        [
          ad.merchant,
          ad.advertisementCode,
          ad.cartProductCode,
          ad.city,
          ad.state,
          ad.pincode,
        ]
          .join(" ")
          .toLowerCase()
          .includes(keyword)
      );
    }

    switch (sortBy) {
      case "LCN_DESC":
        list.sort((a, b) => b.lcn - a.lcn);
        break;
      case "MERCHANT":
        list.sort((a, b) => a.merchant.localeCompare(b.merchant));
        break;
      case "NEWEST":
        list.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        break;
      default:
        list.sort((a, b) => a.lcn - b.lcn);
    }

    return list;
  }, [advertisements, search, channelFilter, statusFilter, sortBy]);

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      {/* ============================================================ */
      /* Header                                                        */
      /* ============================================================ */}
      <div className="sticky top-0 z-40 bg-[#050816]/95 backdrop-blur border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* <Link
              to="/filtv/dashboard"
              className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center hover:bg-white/20"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </Link> */}

            <div>
              <h1 className="text-xl font-bold text-cyan-300">
                FiLTV Advertisement Manager
              </h1>
              <p className="text-xs text-slate-300">
                Manage merchant advertisements by channel
              </p>
            </div>
          </div>

          <button
            onClick={openAddForm}
            className="bg-cyan-600 hover:bg-cyan-500 rounded-xl w-fit px-3 py-2 flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faPlus} />
            Add Advertisement
          </button>
        </div>
      </div>

      {/* ============================================================ */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Message */}
        {message && (
          <div
            className={`mb-5 rounded-xl p-4 border flex items-center gap-3 ${
              message.type === "success"
                ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                : "border-red-500/30 bg-red-500/10 text-red-300"
            }`}
          >
            <FontAwesomeIcon
              icon={
                message.type === "success"
                  ? faCheckCircle
                  : faCircleExclamation
              }
            />
            {message.text}
          </div>
        )}

        {/* ============================================================ */}
        {/* Statistics */}
        {/* ============================================================ */}
        <div className="grid md:grid-cols-4 gap-4 mb-6">
          <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
            <div className="text-xs text-slate-400">Total Ads</div>
            <div className="text-3xl font-bold">{totalAds}</div>
          </div>

          <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
            <div className="text-xs text-slate-400">Channels Used</div>
            <div className="text-3xl font-bold text-cyan-300">
              {uniqueChannels}
            </div>
          </div>

          <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
            <div className="text-xs text-slate-400">Active</div>
            <div className="text-3xl font-bold text-green-400">
              {activeAds}
            </div>
          </div>

          <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
            <div className="text-xs text-slate-400">Inactive</div>
            <div className="text-3xl font-bold text-red-400">
              {inactiveAds}
            </div>
          </div>
        </div>

        {/* ============================================================ */}
        {/* Filters */}
        {/* ============================================================ */}
        <div className="rounded-2xl bg-white/5 border border-white/10 p-4 mb-6">
          <div className="grid lg:grid-cols-4 gap-3">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search Merchant / Product / Ad Code"
              className="bg-[#0b1120] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-cyan-500"
            />

            <select
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value)}
              className="bg-[#0b1120] border border-white/10 rounded-xl px-3 py-1"
            >
              <option>All</option>
              {channels.map((channel) => (
                <option key={channel.lcn} value={channel.lcn}>
                  {channel.lcn} {channel.emoji} {channel.title}
                </option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-[#0b1120] border border-white/10 rounded-xl px-3 py-1"
            >
              <option>All</option>
              <option>Active</option>
              <option>Inactive</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-[#0b1120] border border-white/10 rounded-xl px-3 py-1"
            >
              <option value="LCN_ASC">LCN ↑</option>
              <option value="LCN_DESC">LCN ↓</option>
              <option value="MERCHANT">Merchant A-Z</option>
              <option value="NEWEST">Recently Added</option>
            </select>
          </div>
        </div>

        {showForm && (
            <FILTVAdManagerForm
                form={form}
                setForm={setForm}
                editingCode={editingCode}
                channels={channels}
                saving={saving}
                onCancel={resetForm}
                onSubmit={handleSubmit}
            />
        )}

        {/* ============================================================ */}
        {/* Advertisement List                                            */}
        {/* ============================================================ */}
        {loading ? (
          <div className="py-16 text-center text-slate-300">
            Loading advertisements...
          </div>
        ) : filteredAds.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-white/10 bg-white/5 p-12 text-center">
            <FontAwesomeIcon
              icon={faTv}
              className="text-5xl text-slate-500 mb-4"
            />
            <h3 className="text-xl font-bold">No Advertisements Found</h3>
            <p className="text-slate-400 mt-2">
              Create your first FiLTV Advertisement.
            </p>
          </div>
        ) : (
          <div className="space-y-5">
            {filteredAds.map((ad) => {
              const channel = getChannel(ad.lcn);

              return (
                <div
                  key={ad.advertisementCode}
                  className="rounded-3xl border border-white/10 bg-white/5 p-5 hover:border-cyan-500/40 transition-all"
                >
                  {/* ------------------------------------------------ */}
                  {/* Header */}
                  {/* ------------------------------------------------ */}
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div>
                      <div className="flex flex-wrap gap-2 items-center">
                        <h3 className="text-xl font-bold text-cyan-300">
                          {ad.merchant}
                        </h3>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            ad.isActive
                              ? "bg-emerald-500/20 text-emerald-300"
                              : "bg-red-500/20 text-red-300"
                          }`}
                        >
                          {ad.isActive ? "ACTIVE" : "INACTIVE"}
                        </span>
                      </div>

                      <div className="text-sm text-slate-400 mt-2">
                        Ad Code : {ad.advertisementCode}
                      </div>
                      <div className="text-sm text-slate-400">
                        Product : {ad.cartProductCode}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => openEditForm(ad)}
                        className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20"
                      >
                        <FontAwesomeIcon icon={faPen} />
                      </button>

                      {ad.isActive ? (
                        <button
                          onClick={() => setConfirmDelete(ad)}
                          className="w-10 h-10 rounded-xl bg-red-500/20 hover:bg-red-500/30 text-red-300"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      ) : (
                        <button
                          onClick={async () => {
                            await fetchData({
                              url: `${FILTV_ADS_URL}/${ad.advertisementCode}`,
                              method: "patch",
                              token: authState?.token,
                              body: { isActive: true },
                            });
                            load();
                          }}
                          className="w-10 h-10 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300"
                        >
                          <FontAwesomeIcon icon={faCheckCircle} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* ------------------------------------------------ */}
                  {/* Channel */}
                  {/* ------------------------------------------------ */}
                  <div className="mt-5 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="rounded-2xl bg-[#0b1120] p-4">
                      <div className="text-xs text-slate-400">Channel</div>
                      <div className="font-bold text-cyan-300 mt-1">
                        {channel
                          ? `${channel.lcn} ${channel.emoji} ${channel.title}`
                          : ad.lcn}
                      </div>
                    </div>

                    <div className="rounded-2xl bg-[#0b1120] p-4">
                      <div className="text-xs text-slate-400">Video Type</div>
                      <div className="font-semibold mt-1">{ad.videoType}</div>
                    </div>

                    <div className="rounded-2xl bg-[#0b1120] p-4">
                      <div className="text-xs text-slate-400">Phone</div>
                      <div className="font-semibold mt-1">
                        {ad.phone || "-"}
                      </div>
                    </div>

                    <div className="rounded-2xl bg-[#0b1120] p-4">
                      <div className="text-xs text-slate-400">WhatsApp</div>
                      <div className="font-semibold mt-1">
                        {ad.whatsapp || "-"}
                      </div>
                    </div>
                  </div>

                  {/* ------------------------------------------------ */}
                  {/* Location */}
                  {/* ------------------------------------------------ */}
                  <div className="mt-5">
                    <div className="text-sm text-cyan-300 font-semibold mb-2">
                      Target Location
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {ad.area && (
                        <span className="px-3 py-1 rounded-full bg-white/10">
                          Area : {ad.area}
                        </span>
                      )}
                      {ad.city && (
                        <span className="px-3 py-1 rounded-full bg-white/10">
                          City : {ad.city}
                        </span>
                      )}
                      {ad.state && (
                        <span className="px-3 py-1 rounded-full bg-white/10">
                          State : {ad.state}
                        </span>
                      )}
                      {ad.pincode && (
                        <span className="px-3 py-1 rounded-full bg-white/10">
                          PIN : {ad.pincode}
                        </span>
                      )}
                      {!ad.area &&
                        !ad.city &&
                        !ad.state &&
                        !ad.pincode && (
                          <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300">
                            🌍 Pan India Advertisement
                          </span>
                        )}
                    </div>
                  </div>

                  {/* ------------------------------------------------ */}
                  {/* Offer */}
                  {/* ------------------------------------------------ */}
                  {ad.offer && (
                    <div className="mt-5 rounded-2xl bg-[#0b1120] p-4">
                      <div className="text-cyan-300 font-semibold mb-2">
                        Offer
                      </div>
                      <div className="text-slate-200">{ad.offer}</div>
                      {ad.offerEndsInMinutes !== null && (
                        <div className="mt-3 text-sm text-orange-300">
                          Ends in {ad.offerEndsInMinutes} minutes
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ============================================================ */}
        {/* Delete Confirmation                                          */}
        {/* ============================================================ */}
        {confirmDelete && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
            <div className="bg-[#0b1120] rounded-3xl p-6 border border-white/10 max-w-md w-full">
              <h3 className="text-xl font-bold text-red-300">
                Deactivate Advertisement?
              </h3>

              <p className="text-slate-400 mt-3">
                <b>{confirmDelete.merchant}</b>
                <br />
                {confirmDelete.advertisementCode}
              </p>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={async () => {
                    await fetchData({
                      url: `${FILTV_ADS_URL}/${confirmDelete.advertisementCode}`,
                      method: "delete",
                      token: authState?.token,
                    });
                    setConfirmDelete(null);
                    load();
                  }}
                  className="flex-1 rounded-xl bg-red-600 hover:bg-red-500 py-3"
                >
                  Deactivate
                </button>

                <button
                  onClick={() => setConfirmDelete(null)}
                  className="flex-1 rounded-xl border border-white/10 py-3 hover:bg-white/10"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FILTVAdManager;